/**
 * useInvestigationSync — debounced backend sync for the active thread.
 *
 * The client-side `investigationStore` is authoritative during a session
 * (sessionStorage), but it is lost when the tab closes and cannot be
 * shared with peer reviewers. This hook subscribes to the store and
 * POSTs the active thread to `/api/v2/brain/investigations` whenever it
 * changes, debounced so rapid edits (typing into Discussion, pinning
 * several artifacts in a row) collapse into a single write.
 *
 * Designed to be mounted ONCE at the app root so we never double-post.
 *
 * Failure mode: best-effort. We log once on failure and back off. The
 * local thread remains usable whether or not the server is reachable;
 * the UI is not blocked on network latency.
 */
import { useEffect, useRef } from 'react'
import { useInvestigationStore } from '../store/investigationStore'
import apiClient from '../services/api'

const DEBOUNCE_MS = 1500

export function useInvestigationSync() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inflightRef = useRef(false)
  const lastSignatureRef = useRef<string>('')

  useEffect(() => {
    const unsub = useInvestigationStore.subscribe((state, prev) => {
      const current = state.active
      const previous = prev.active

      // Both null → nothing to sync.
      if (!current && !previous) return

      // Transition to null = closed thread. Don't keep syncing.
      if (!current) return

      // Identity change (new thread opened) → reset signature so we post.
      if (!previous || previous.id !== current.id) {
        lastSignatureRef.current = ''
      }

      // Cheap change detection — only post when something actually changed.
      const signature = JSON.stringify({
        id: current.id,
        steps: current.steps.length,
        artifacts: current.artifacts.length,
        status: current.status,
        discussion: current.discussion,
        recommendation: current.recommendation,
        reviewers: current.reviewers,
      })
      if (signature === lastSignatureRef.current) return

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(async () => {
        if (inflightRef.current) return
        inflightRef.current = true
        try {
          await apiClient.post('/api/v2/brain/investigations', current)
          lastSignatureRef.current = signature
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[useInvestigationSync] background sync failed', e)
        } finally {
          inflightRef.current = false
        }
      }, DEBOUNCE_MS)
    })

    return () => {
      unsub()
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])
}

export default useInvestigationSync
